export type GlyphIconName =
  | 'arrow'
  | 'bell'
  | 'chat'
  | 'city'
  | 'close'
  | 'home'
  | 'heart'
  | 'heart-filled'
  | 'guitar'
  | 'links'
  | 'loc-profile'
  | 'menu'
  | 'minus'
  | 'passport'
  | 'pause'
  | 'play'
  | 'plus'
  | 'punk-drummer'
  | 'rock-horns'
  | 'star'
  | 'ticket'
  | 'tour'
  | 'user'
  | 'drumstick-plus'

interface GlyphIconProps {
  className?: string
  name: GlyphIconName
}

export function GlyphIcon({ className, name }: GlyphIconProps) {
  const common = {
    'aria-hidden': true,
    className,
    'data-glyph': name,
    focusable: false,
    viewBox: '0 0 24 24',
  } as const

  if (name === 'city') {
    return <svg {...common}><path d="M12 3 21 12 12 21 3 12Z" fill="currentColor" /></svg>
  }

  if (name === 'passport') {
    return <svg {...common}><circle cx="12" cy="12" fill="none" r="8.5" stroke="currentColor" strokeWidth="2" /><path d="m12 6 1.7 3.6 4 .4-3 2.8.8 4-3.5-2-3.5 2 .8-4-3-2.8 4-.4Z" fill="currentColor" /></svg>
  }

  if (name === 'star') {
    return <svg {...common}><path d="m12 2.8 2.62 5.31 5.86.85-4.24 4.13 1 5.84L12 16.17l-5.24 2.76 1-5.84-4.24-4.13 5.86-.85Z" fill="currentColor" /></svg>
  }

  if (name === 'home') {
    return <svg {...common}><path d="m3 11 9-8 9 8-2 10H5Z" fill="none" stroke="currentColor" strokeLinejoin="miter" strokeWidth="2" /><path d="M9 21v-7h6v7M4 7l4-3" stroke="currentColor" strokeWidth="2" /></svg>
  }

  if (name === 'heart' || name === 'heart-filled') {
    return (
      <svg {...common}>
        <path
          d="M12 20.4 4.2 13A5 5 0 0 1 11.3 6L12 6.8l.7-.8a5 5 0 0 1 7.1 7Z"
          fill={name === 'heart-filled' ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeLinejoin="miter"
          strokeWidth="2"
        />
      </svg>
    )
  }

  if (name === 'tour') {
    return (
      <svg {...common} data-tour-vehicle="bus">
        <rect fill="none" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" width="17" x="3.5" y="4.5" />
        <path d="M6 7h12v5H6ZM4 14.5h16M8 18v2m8-2v2" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.8" />
        <circle cx="8" cy="18" fill="currentColor" r="1.5" />
        <circle cx="16" cy="18" fill="currentColor" r="1.5" />
        <path d="M9.5 7v5m5-5v5" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    )
  }

  if (name === 'ticket') {
    return <svg {...common}><path d="M3 7h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4Z" fill="none" stroke="currentColor" strokeWidth="2" /><path d="m9 16 3-8 1 4 4-1-5 6Z" fill="currentColor" /></svg>
  }

  if (name === 'chat') {
    return <svg {...common}><path d="M4 4h16l-1 12H9l-5 4Z" fill="none" stroke="currentColor" strokeWidth="2" /><path d="m11 7 2 3 3-1-2 4" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
  }

  if (name === 'bell') {
    return <svg {...common}><path d="M6 17h12l-2-3V9a4 4 0 0 0-8 0v5Zm4 3h4" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
  }

  if (name === 'play') {
    return <svg {...common}><path d="m8 5 11 7-11 7Z" fill="currentColor" /></svg>
  }

  if (name === 'pause') {
    return <svg {...common}><path d="M7 5h4v14H7zm6 0h4v14h-4z" fill="currentColor" /></svg>
  }

  if (name === 'guitar') {
    return (
      <svg {...common}>
        <path d="M8.5 12.2c2.2-1.5 5.3-.9 6.7 1.3 1.6 2.5.6 5.8-2.1 7.1-2.6 1.3-5.9.1-7-2.6-1-2.4 0-4.6 2.4-5.8Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="m12.5 13.3 7.2-9.1 1.7 1.3-7.2 9.1M18.1 6.2l2.4 1.9M7.2 17.4l3-3" fill="none" stroke="currentColor" strokeLinecap="square" strokeWidth="1.8" />
        <circle cx="10.2" cy="16.4" fill="currentColor" r="1.2" />
      </svg>
    )
  }

  if (name === 'punk-drummer') {
    return (
      <svg {...common}>
        <path d="m4 4 16 16M20 4 4 20" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
        <path d="m6 3-2 1m14-1 2 1" stroke="currentColor" strokeLinecap="round" strokeWidth="2.2" />
        <path d="m6 11 2-5 3 2 2-5 2 5 3-2v6" fill="currentColor" />
        <circle cx="12" cy="12" fill="none" r="4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M6 22c.8-4.5 2.8-6.5 6-6.5s5.2 2 6 6.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    )
  }

  if (name === 'rock-horns') {
    return (
      <svg {...common} data-hand-sign="rock-horns">
        <path d="M8 12V4.5a1.6 1.6 0 0 1 3.2 0v7.2M17 12V5a1.6 1.6 0 0 1 3.2 0v10.2c0 4.4-2.4 6.8-6.5 6.8H12c-3.7 0-6.2-2.2-6.2-6.2v-4.9L4.2 8.2a1.6 1.6 0 0 1 2.7-1.7L9 10" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
        <path d="M11.2 11.8V9.2a1.5 1.5 0 0 1 3 0v2.6M14.2 11.8V10a1.4 1.4 0 0 1 2.8 0v2" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        <path d="M9 14.2c2.7-1.5 5.3-1.5 8 0" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
      </svg>
    )
  }

  if (name === 'drumstick-plus') {
    return (
      <svg {...common}>
        <path d="m5.2 18.8 12.9-13M5.2 5.2l12.9 13" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2.3" />
        <ellipse cx="19.2" cy="4.6" fill="currentColor" rx="2.4" ry="1.6" transform="rotate(-42 19.2 4.6)" />
        <ellipse cx="19.2" cy="19.4" fill="currentColor" rx="2.4" ry="1.6" transform="rotate(42 19.2 19.4)" />
        <path d="m4.1 20 2.3-2.3M4.1 4l2.3 2.3" stroke="currentColor" strokeLinecap="round" strokeWidth="3.2" />
      </svg>
    )
  }

  if (name === 'loc-profile') {
    return (
      <svg {...common} data-profile-shape="short-locs">
        <circle cx="12" cy="8.5" fill="none" r="3.6" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.8 21c.8-4.6 3.2-6.9 7.2-6.9s6.4 2.3 7.2 6.9" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
        <path d="M8.2 7.4C8.5 4.3 10 2.8 12.3 2.8c2.7 0 4.4 1.8 4.2 5.2M8.7 4.8l-.8 6.4m3-8-.5 7.9m2.8-8-.1 8.2m2.3-7.1.5 6.6" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.45" />
      </svg>
    )
  }

  if (name === 'user') {
    return <svg {...common}><circle cx="12" cy="8" fill="none" r="4" stroke="currentColor" strokeWidth="2" /><path d="M4 21a8 8 0 0 1 16 0" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
  }

  if (name === 'menu') {
    return <svg {...common}><path d="M4 6h16M4 12h16M4 18h16" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
  }

  const paths: Record<Exclude<GlyphIconName, 'city' | 'passport' | 'star' | 'home' | 'heart' | 'heart-filled' | 'tour' | 'ticket' | 'chat' | 'bell' | 'play' | 'pause' | 'guitar' | 'punk-drummer' | 'rock-horns' | 'drumstick-plus' | 'loc-profile' | 'user' | 'menu'>, string> = {
    arrow: 'M7 17 17 7M9 7h8v8',
    close: 'M6 6l12 12M18 6 6 18',
    links: 'M7 17 17 7M9 7h8v8',
    minus: 'M5 12h14',
    plus: 'M12 5v14M5 12h14',
  }

  return <svg {...common}><path d={paths[name]} fill="none" stroke="currentColor" strokeLinecap="square" strokeWidth="2.25" /></svg>
}
